/**
 * OrderSubmission.js (v3 — Mobile PWA)
 * Raider Depot — Client Order Submission Screen
 * Route: /order (public, no auth required per ADR-001)
 *
 * Changes from v2:
 * - QR code scanner for token entry (P.3)
 * - Mobile-first layout: max-width 480px, full-width on small screens
 * - Touch-optimized qty controls: 48px tap targets
 * - Success screen: large QR display optimized for screenshot/show at pickup
 * - CSS media query via viewport meta — no changes to logic
 */

import React, { useState, useEffect, useCallback } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import QRScanner from "./QRScanner";
import QRCode from "react-qr-code"; // already in project from registration screen
import NavBar from "../components/NavBar";

// ── Constants ──────────────────────────────────────────────────────────────
const ORDER_API_URL = "/api/orders";
const CATALOG_URL = "/api/orders/catalog";
const HOUSEHOLD_API_URL = "/api/households";
const ACCOUNT_API_URL = "/api/accounts";
const TOKEN_STORAGE_KEY = "raider-depot-token";

const NAVY = "#1A3B6E";
const MID = "#2C5FA8";
const LIGHT = "#E8F2FB";

// Categories derived dynamically from catalog data — no hardcoded whitelist.
// Adding a new category = adding items with that category string. Zero code changes.

// ── Styles ─────────────────────────────────────────────────────────────────
const S = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#F7FAFD",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "16px 12px 100px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 16,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    padding: "24px 20px",
    maxWidth: 480,
    width: "100%",
    marginBottom: 16,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: NAVY,
    textAlign: "center",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: 14,
    color: "#64748b",
    textAlign: "center",
    margin: "0 0 20px 0",
    lineHeight: 1.5,
  },
  privacyBanner: {
    backgroundColor: LIGHT,
    borderRadius: 10,
    padding: "10px 14px",
    textAlign: "center",
    fontSize: 13,
    color: MID,
    marginBottom: 16,
  },
  // Token input — large, centered, easy to type on phone
  tokenInput: {
    width: "100%",
    padding: "16px",
    fontSize: 22,
    fontWeight: 700,
    textAlign: "center",
    border: `2px solid #e2e8f0`,
    borderRadius: 10,
    outline: "none",
    letterSpacing: 3,
    textTransform: "uppercase",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
    WebkitAppearance: "none",
  },
  tokenInputValid: { borderColor: "#22c55e" },
  tokenInputError: { borderColor: "#ef4444" },
  // Primary button — full width, 52px tall (thumb-friendly)
  buttonPrimary: {
    width: "100%",
    padding: "16px",
    fontSize: 16,
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: NAVY,
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    marginTop: 12,
    WebkitAppearance: "none",
    minHeight: 52,
  },
  buttonDisabled: { backgroundColor: "#94a3b8", cursor: "not-allowed" },
  // QR scan button
  qrScanButton: {
    width: "100%",
    padding: "14px",
    fontSize: 15,
    fontWeight: 600,
    color: NAVY,
    backgroundColor: LIGHT,
    border: `1.5px solid ${MID}`,
    borderRadius: 10,
    cursor: "pointer",
    marginTop: 8,
    marginBottom: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 52,
    WebkitAppearance: "none",
  },
  // Catalog item row — tall enough for easy tapping
  itemRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 0",
    borderBottom: "1px solid #f1f5f9",
    minHeight: 56,
  },
  itemName: { fontSize: 15, color: "#1e293b", flex: 1, paddingRight: 8 },
  // Qty controls — 48px tap targets
  qtyButton: {
    width: 44,
    height: 44,
    borderRadius: 10,
    border: `1.5px solid #cbd5e1`,
    backgroundColor: "#ffffff",
    fontSize: 22,
    fontWeight: 700,
    color: "#334155",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    WebkitAppearance: "none",
  },
  qtyValue: {
    width: 36,
    textAlign: "center",
    fontSize: 18,
    fontWeight: 700,
    color: "#1e293b",
  },
  addButton: {
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 600,
    color: MID,
    border: `1.5px solid ${MID}`,
    borderRadius: 10,
    backgroundColor: "#ffffff",
    cursor: "pointer",
    minHeight: 44,
    WebkitAppearance: "none",
  },
  maxLabel: {
    fontSize: 11,
    color: "#94a3b8",
    textAlign: "center",
    marginTop: 3,
    whiteSpace: "nowrap",
  },
  categoryHeader: {
    fontSize: 15,
    fontWeight: 700,
    color: "#334155",
    margin: "20px 0 8px 0",
    padding: "0 0 6px 0",
    borderBottom: `2px solid #e2e8f0`,
  },
  // Sticky submit bar at bottom
  summaryBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: NAVY,
    color: "#ffffff",
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 -4px 20px rgba(0,0,0,0.2)",
    zIndex: 100,
    paddingBottom: "max(14px, env(safe-area-inset-bottom))",
  },
  summaryText: { fontSize: 15, fontWeight: 500 },
  submitButton: {
    padding: "12px 24px",
    fontSize: 15,
    fontWeight: 700,
    color: NAVY,
    backgroundColor: "#fbbf24",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    minHeight: 48,
    WebkitAppearance: "none",
  },
  errorText: {
    color: "#ef4444",
    fontSize: 14,
    textAlign: "center",
    marginTop: 8,
  },
  tokenBadge: {
    display: "inline-block",
    backgroundColor: LIGHT,
    color: NAVY,
    padding: "6px 16px",
    borderRadius: 8,
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: 1,
    marginBottom: 12,
  },
  loading: {
    textAlign: "center",
    padding: "40px",
    color: "#64748b",
    fontSize: 16,
  },
};

export default function OrderSubmission() {
  const [phase, setPhase] = useState("loading");
  const [token, setToken] = useState("");
  const [tokenValid, setTokenValid] = useState(null);
  const [tokenError, setTokenError] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [hasSavedToken, setHasSavedToken] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  // Feedback (B.1)
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");

  // Auth (Tier 2)
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState(false);

  // Active cycle
  const [activeCycle, setActiveCycle] = useState(null);
  const [cycleMessage, setCycleMessage] = useState(null);
  const [cycleLoading, setCycleLoading] = useState(true);

  // ── Auth listener ──────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u && !u.isAnonymous) {
        setUser(u);
        try {
          const idToken = await u.getIdToken();
          const res = await fetch(`${ACCOUNT_API_URL}/me`, {
            headers: { Authorization: `Bearer ${idToken}` },
          });
          if (res.ok) {
            const data = await res.json();
            if (data.tokenId) {
              setToken(data.tokenId);
              setTokenValid(true);
              setAuthMode(true);
              await loadCatalog();
              setPhase("welcome");
              return;
            }
          }
        } catch (e) {}
      }
      const pathParts = window.location.pathname.split("/");
      const pathToken =
        pathParts[1] === "order" && pathParts[2]
          ? decodeURIComponent(pathParts[2])
          : null;
      const params = new URLSearchParams(window.location.search);
      const urlToken = pathToken || params.get("token");
      if (urlToken) {
        setToken(urlToken.toUpperCase());
      } else {
        try {
          const saved = localStorage.getItem(TOKEN_STORAGE_KEY);
          if (saved) {
            setToken(saved);
            setHasSavedToken(true);
          }
        } catch (e) {}
      }
      setPhase("token");
    });
    return unsub;
  }, []);

  // ── Load active cycle ──────────────────────────────────────────────────
  useEffect(() => {
    const loadActiveCycle = async () => {
      try {
        const res = await fetch("/api/cycles/active");
        if (res.ok) {
          const { cycle, message } = await res.json();
          setActiveCycle(cycle);
          setCycleMessage(message);
        } else {
          setActiveCycle(null);
        }
      } catch (err) {
        console.error("Active cycle load error:", err);
        setActiveCycle(null);
      } finally {
        setCycleLoading(false);
      }
    };
    loadActiveCycle();
  }, []);

  // ── QR scan handler ────────────────────────────────────────────────────
  const handleQRScan = useCallback((scannedToken) => {
    setShowScanner(false);
    setToken(scannedToken);
    setTokenValid(null);
    setTokenError("");
  }, []);

  // ── Token validation ───────────────────────────────────────────────────
  const validateToken = useCallback(
    async (overrideToken) => {
      const t = overrideToken || token;
      if (!t || t.length < 6) {
        setTokenError("Please enter your Raider Depot token (e.g. RD-RW-001)");
        setTokenValid(false);
        return;
      }
      setLoading(true);
      setTokenError("");
      setTokenValid(null);
      try {
        const res = await fetch(`${HOUSEHOLD_API_URL}/${t}`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === "active" && data.cycleEligible) {
            setTokenValid(true);
            try {
              localStorage.setItem(TOKEN_STORAGE_KEY, t);
            } catch (e) {}
            await loadCatalog();
            setPhase("welcome");
          } else if (!data.cycleEligible) {
            setTokenValid(false);
            setTokenError("This token is not eligible for the current cycle.");
          } else {
            setTokenValid(false);
            setTokenError(
              "This token is not active. Please contact your school counselor.",
            );
          }
        } else if (res.status === 404) {
          setTokenValid(false);
          setTokenError(
            "Token not found. Please check your token and try again.",
          );
        } else {
          setTokenValid(false);
          setTokenError("Something went wrong. Please try again.");
        }
      } catch (err) {
        setTokenValid(false);
        setTokenError(
          "Unable to connect. Please check your internet and try again.",
        );
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  // Auto-validate after QR scan populates token
  useEffect(() => {
    if (token && phase === "token" && tokenValid === null && !showScanner) {
      const t = token.trim().toUpperCase();
      if (t.match(/^RD-[A-Z]{2}-\d{3,}$/)) {
        validateToken(t);
      }
    }
  }, [token, phase, tokenValid, showScanner, validateToken]);

  const loadCatalog = async () => {
    try {
      const res = await fetch(CATALOG_URL);
      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data) ? data : data.items || [];
        setCatalog(items.filter((item) => item.inStock));
      }
    } catch (err) {
      console.error("Catalog fetch error:", err);
    }
  };

  const incrementQty = (itemId) => {
    const catalogItem = catalog.find((c) => c.itemId === itemId);
    const max = catalogItem?.maxQtyPerOrder ?? 5;
    setQuantities((prev) => ({
      ...prev,
      [itemId]: Math.min((prev[itemId] || 0) + 1, max),
    }));
  };

  const decrementQty = (itemId) =>
    setQuantities((prev) => {
      const current = prev[itemId] || 0;
      if (current <= 1) {
        const next = { ...prev };
        delete next[itemId];
        return next;
      }
      return { ...prev, [itemId]: current - 1 };
    });

  const selectedItems = Object.entries(quantities).filter(([, qty]) => qty > 0);
  const totalItems = selectedItems.reduce((sum, [, qty]) => sum + qty, 0);

  const groupedCatalog = (() => {
    const groups = {};
    catalog.forEach((item) => {
      const cat = item.category || "Other";
      if (!groups[cat]) groups[cat] = { key: cat, label: cat, items: [] };
      groups[cat].items.push(item);
    });
    return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label));
  })();

  const submitOrder = async () => {
    if (selectedItems.length === 0) return;
    if (!activeCycle) {
      setSubmitError(
        "Ordering is currently closed. Please check back when the next cycle opens.",
      );
      return;
    }
    setPhase("submitting");
    setSubmitError("");
    const orderPayload = {
      tokenId: token,
      cycleId: activeCycle.cycleId,
      items: selectedItems.map(([itemId, qty]) => ({ itemId, qty })),
    };
    try {
      const res = await fetch(ORDER_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderPayload),
      });
      if (res.ok) {
        const data = await res.json();
        setOrderResult({
          orderId: data.orderId,
          items: selectedItems.map(([itemId, qty]) => {
            const catalogItem = catalog.find((c) => c.itemId === itemId);
            return { name: catalogItem?.name || itemId, qty };
          }),
        });
        setPhase("success");
      } else if (res.status === 409) {
        const errData = await res.json().catch(() => ({}));
        if (errData.error === 'cycle_closed') {
          setSubmitError("Ordering for this cycle has closed. Please contact your counselor.");
        } else {
          setSubmitError("duplicate");
        }
        setPhase("catalog");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const errData = await res.json().catch(() => ({}));
        setSubmitError(
          errData.error || "Order submission failed. Please try again.",
        );
        setPhase("catalog");
      }
    } catch (err) {
      setSubmitError(
        "Unable to connect. Please check your internet and try again.",
      );
      setPhase("catalog");
    }
  };

  const clearSavedToken = () => {
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch (e) {}
    setToken("");
    setHasSavedToken(false);
    setTokenValid(null);
    setAuthMode(false);
    setPhase("token");
  };

  const submitFeedback = async () => {
    if (!feedbackRating && !feedbackComment) return;
    setFeedbackSending(true);
    setFeedbackError("");
    try {
      const res = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tokenId: token || null,
          uid: user?.uid || null,
          role: "client",
          feedbackType: "order",
          rating: feedbackRating,
          comment: feedbackComment || null,
          cycleId: activeCycle?.cycleId || "2026-04-14",
        }),
      });
      if (res.ok) {
        setFeedbackSent(true);
      } else {
        setFeedbackError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setFeedbackError("Something went wrong. Please try again.");
    } finally {
      setFeedbackSending(false);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    clearSavedToken();
  };

  // ── Loading ────────────────────────────────────────────────────────────
  if (phase === "loading" || cycleLoading) {
    return (
      <div style={S.container}>
        <NavBar title="Place an Order" />
        <div style={S.loading}>Loading...</div>
      </div>
    );
  }

  // ── No active cycle ────────────────────────────────────────────────────
  if (!activeCycle) {
    return (
      <div style={S.container}>
        <NavBar title="Place an Order" />
        <div style={S.card}>
          <h1 style={S.title}>Ordering Closed</h1>
          <p style={S.subtitle}>
            {cycleMessage || 'Ordering is currently closed. Please check back when the next cycle opens.'}
          </p>
          <div style={S.privacyBanner}>
            Your privacy is protected. This system is anonymous by design.
          </div>
        </div>
      </div>
    );
  }

  // ── Token entry ────────────────────────────────────────────────────────
  if (phase === "token") {
    return (
      <div style={S.container}>
        <NavBar title="Place an Order" />
        <div style={S.card}>
          <h1 style={S.title}>Raider Depot</h1>
          <p style={S.subtitle}>
            Enter your token or scan your QR card to place your order.
            <br />
            No personal information is collected.
          </p>
          <div style={S.privacyBanner}>
            Your privacy is protected. This system is anonymous by design.
          </div>

          {/* QR Scanner */}
          {showScanner ? (
            <div style={{ marginBottom: 16 }}>
              <QRScanner
                active={showScanner}
                onScan={handleQRScan}
                onError={() => setShowScanner(false)}
                onClose={() => setShowScanner(false)}
              />
            </div>
          ) : (
            <button style={S.qrScanButton} onClick={() => setShowScanner(true)}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke={MID}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
                <path d="M14 14h3v3M17 17h3v3M14 20h3" />
              </svg>
              Scan QR code
            </button>
          )}

          <div
            style={{
              textAlign: "center",
              fontSize: 12,
              color: "#94a3b8",
              margin: "8px 0",
            }}
          >
            — or enter manually —
          </div>

          {hasSavedToken && (
            <div
              style={{
                backgroundColor: "#f0fdf4",
                borderRadius: 10,
                padding: "10px 14px",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 13, color: "#16a34a" }}>
                Saved token found
              </span>
              <button
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  textDecoration: "underline",
                  padding: 0,
                }}
                onClick={clearSavedToken}
              >
                Use different token
              </button>
            </div>
          )}

          <input
            style={{
              ...S.tokenInput,
              ...(tokenValid === true
                ? S.tokenInputValid
                : tokenValid === false
                  ? S.tokenInputError
                  : {}),
            }}
            type="text"
            placeholder="RD-RW-001"
            value={token}
            onChange={(e) => {
              setToken(e.target.value.toUpperCase());
              setTokenValid(null);
              setTokenError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && validateToken()}
            autoComplete="off"
            autoCapitalize="characters"
          />
          {tokenError && <p style={S.errorText}>{tokenError}</p>}

          <button
            style={{ ...S.buttonPrimary, ...(loading ? S.buttonDisabled : {}) }}
            onClick={() => validateToken()}
            disabled={loading}
          >
            {loading ? "Checking..." : "Continue to Order"}
          </button>

          <div
            style={{
              textAlign: "center",
              marginTop: 16,
              fontSize: 13,
              color: "#94a3b8",
            }}
          >
            Have an account?{" "}
            <a href="/login" style={{ color: MID }}>
              Sign in for faster ordering
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Welcome / Token recognized interstitial ──────────────────────────
  if (phase === "welcome") {
    return (
      <div style={S.container}>
        <NavBar title="Place an Order" />
        <div style={{ ...S.card, textAlign: "center", overflow: "hidden" }}>
          {/* Animated checkmark */}
          <div style={{
            width: 80, height: 80, borderRadius: "50%",
            backgroundColor: "#f0fdf4", border: "3px solid #22c55e",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 20px",
            animation: "scaleIn 0.5s ease-out",
          }}>
            <span style={{ fontSize: 40, lineHeight: 1 }}>✓</span>
          </div>

          <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, margin: "0 0 8px" }}>
            Welcome to Raider Depot
          </h2>
          <p style={{ fontSize: 15, color: "#64748b", margin: "0 0 20px", lineHeight: 1.5 }}>
            Your token has been verified. You are ready to select items for your household.
          </p>

          {/* Token badge */}
          <div style={{
            display: "inline-block", backgroundColor: LIGHT,
            color: NAVY, padding: "8px 20px", borderRadius: 10,
            fontSize: 20, fontWeight: 700, letterSpacing: 2, marginBottom: 24,
          }}>
            {token}
          </div>

          {/* Privacy reminder */}
          <div style={{
            backgroundColor: "#f8fafc", borderRadius: 10,
            padding: "12px 16px", marginBottom: 24,
            fontSize: 13, color: "#64748b", lineHeight: 1.5,
          }}>
            🔒 Your privacy is protected. No personal information is stored.
            <br />Your token is your only identifier.
          </div>

          {/* Proceed button */}
          <button
            onClick={() => setPhase("catalog")}
            style={{
              ...S.buttonPrimary,
              fontSize: 17, padding: "16px 32px",
              display: "flex", alignItems: "center",
              justifyContent: "center", gap: 8,
            }}
          >
            Start Your Order →
          </button>
        </div>

        {/* Keyframe animation */}
        <style>{`
          @keyframes scaleIn {
            0% { transform: scale(0); opacity: 0; }
            60% { transform: scale(1.15); }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  // ── Success / QR display ───────────────────────────────────────────────
  if (phase === "success" && orderResult) {
    const qrValue = `https://riverwoodraiderdepot.org/order/${token}`;

    // B-3: Format pickup date from active cycle
    const pickupDateStr = (() => {
      try {
        if (!activeCycle?.pickupDate) return null;
        const d = new Date(activeCycle.pickupDate);
        if (isNaN(d)) return null;
        return d.toLocaleDateString("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
        });
      } catch {
        return null;
      }
    })();

    return (
      <div style={S.container}>
        <NavBar title="Place an Order" />
        <div style={{ ...S.card, textAlign: "center" }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>✅</div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "#16a34a",
              marginBottom: 4,
            }}
          >
            Order Submitted!
          </h2>
          <p style={{ color: "#64748b", fontSize: 14, marginBottom: 20 }}>
            Show this QR code at pickup.
            {pickupDateStr
              ? ` Your bag will be ready on ${pickupDateStr}.`
              : " Your bag will be ready on distribution day."}
          </p>

          {/* Large QR code — optimized for showing on phone screen */}
          <div
            style={{
              background: "#fff",
              border: `3px solid ${NAVY}`,
              borderRadius: 16,
              padding: 20,
              display: "inline-block",
              marginBottom: 16,
            }}
          >
            <QRCode value={qrValue} size={200} level="M" />
          </div>

          <div style={S.tokenBadge}>{token}</div>

          <p style={{ fontSize: 13, color: "#94a3b8", marginBottom: 20 }}>
            Screenshot this screen to save your QR code.
          </p>

          {/* Order summary */}
          <div style={{ textAlign: "left", marginBottom: 20 }}>
            <h3
              style={{
                fontSize: 13,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: 1,
                marginBottom: 8,
              }}
            >
              Order Summary
            </h3>
            {orderResult.items.map(({ name, qty }) => (
              <div
                key={name}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  fontSize: 14,
                  color: "#475569",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <span>{name}</span>
                <span style={{ fontWeight: 700 }}>×{qty}</span>
              </div>
            ))}
          </div>

          <p style={{ color: "#94a3b8", fontSize: 12, marginBottom: 20 }}>
            Order ID: {orderResult.orderId}
          </p>

          {/* Pickup Instructions — B-3: dynamic from cycle document */}
          <div
            style={{
              padding: 16,
              backgroundColor: "#eff6ff",
              borderRadius: 12,
              border: "1px solid #bfdbfe",
              textAlign: "left",
              marginBottom: 20,
            }}
          >
            <h3
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: NAVY,
                margin: "0 0 12px 0",
              }}
            >
              Pick-Up Information
            </h3>
            <p
              style={{
                fontSize: 13,
                color: "#334155",
                margin: "0 0 14px 0",
                lineHeight: 1.5,
              }}
            >
              {pickupDateStr
                ? `Pick-up will be available on ${pickupDateStr}.`
                : "Pick-up details will be shared soon."}{" "}
              Please be prepared to show your token ID or QR code at pick-up.
            </p>

            {activeCycle?.pickupWindows && (
              <div
                style={{
                  fontSize: 13,
                  color: "#334155",
                  lineHeight: 1.8,
                  marginBottom: 14,
                  whiteSpace: "pre-line",
                }}
              >
                {activeCycle.pickupWindows}
              </div>
            )}

            {activeCycle?.pickupLocation && (
              <p
                style={{ fontSize: 12, color: "#64748b", margin: "0 0 12px 0" }}
              >
                📍 {activeCycle.pickupLocation}
              </p>
            )}

            <p
              style={{
                fontSize: 12,
                color: "#334155",
                margin: "12px 0 0 0",
                borderTop: "1px solid #bfdbfe",
                paddingTop: 10,
              }}
            >
              Have questions? Reach out to{" "}
              <a
                href="mailto:orders@riverwoodraiderdepot.org"
                style={{
                  color: "#1e40af",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                orders@riverwoodraiderdepot.org
              </a>
            </p>
          </div>

          {/* Feedback (B.1) */}
          {!feedbackSent ? (
            <div
              style={{
                padding: 16,
                backgroundColor: "#f8fafc",
                borderRadius: 12,
                border: "1px solid #e2e8f0",
                textAlign: "left",
              }}
            >
              <p
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#334155",
                  margin: "0 0 10px",
                }}
              >
                How was your experience?
              </p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setFeedbackRating(star)}
                    style={{
                      fontSize: 32,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      opacity:
                        feedbackRating && star <= feedbackRating ? 1 : 0.25,
                    }}
                  >
                    ★
                  </button>
                ))}
              </div>
              <textarea
                value={feedbackComment}
                onChange={(e) =>
                  setFeedbackComment(e.target.value.slice(0, 500))
                }
                placeholder="Any suggestions? Items you'd like to see? (optional)"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  fontSize: 13,
                  resize: "vertical",
                  minHeight: 60,
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 8,
                }}
              >
                <button
                  onClick={submitFeedback}
                  disabled={
                    feedbackSending || (!feedbackRating && !feedbackComment)
                  }
                  style={{
                    padding: "10px 20px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor:
                      feedbackRating || feedbackComment ? NAVY : "#94a3b8",
                    border: "none",
                    borderRadius: 8,
                    cursor:
                      feedbackRating || feedbackComment
                        ? "pointer"
                        : "not-allowed",
                    minHeight: 44,
                  }}
                >
                  {feedbackSending ? "Sending..." : "Send Feedback"}
                </button>
              </div>
              {feedbackError && (
                <p style={{ fontSize: 13, color: "#dc2626", marginTop: 8, marginBottom: 0 }}>
                  {feedbackError}
                </p>
              )}
            </div>
          ) : (
            <div
              style={{
                padding: 14,
                backgroundColor: "#f0fdf4",
                borderRadius: 12,
                textAlign: "center",
              }}
            >
              <p style={{ fontSize: 14, color: "#16a34a", margin: 0 }}>
                Thank you for your feedback!
              </p>
            </div>
          )}

          {authMode && (
            <a
              href="/my-orders"
              style={{
                display: "block",
                marginTop: 16,
                fontSize: 14,
                color: MID,
              }}
            >
              View your order history →
            </a>
          )}
        </div>
      </div>
    );
  }

  // ── Catalog / Submitting ───────────────────────────────────────────────
  return (
    <div style={S.container}>
      <NavBar title="Place an Order" />
      <div style={S.card}>
        {authMode && user && (
          <div
            style={{
              backgroundColor: "#f0fdf4",
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: 13, color: "#16a34a" }}>
              Signed in as {user.email}
            </span>
            <button
              style={{
                fontSize: 12,
                color: "#64748b",
                background: "none",
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
                padding: 0,
              }}
              onClick={handleSignOut}
            >
              Sign out
            </button>
          </div>
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <div>
            <h1
              style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: 0 }}
            >
              Select Your Items
            </h1>
            <p style={{ color: "#64748b", fontSize: 13, margin: "2px 0 0" }}>
              Choose what you need for this cycle
            </p>
          </div>
          <div style={S.tokenBadge}>{token}</div>
        </div>

        {submitError && (
          <div
            style={{
              backgroundColor: "#fef2f2",
              borderRadius: 10,
              padding: "14px 16px",
              marginBottom: 14,
            }}
          >
            {submitError === "duplicate" ? (
              <>
                <p
                  style={{
                    color: "#dc2626",
                    fontSize: 14,
                    fontWeight: 600,
                    margin: "0 0 6px",
                  }}
                >
                  You have already placed an order this cycle.
                </p>
                <p
                  style={{ color: "#dc2626", fontSize: 13, margin: "0 0 10px" }}
                >
                  One order per cycle is allowed. If you need to make changes,
                  contact the school office.
                </p>
                <a
                  href="/my-orders"
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#2563eb",
                    textDecoration: "none",
                  }}
                >
                  View your previous order →
                </a>
              </>
            ) : (
              <p style={{ color: "#dc2626", fontSize: 14, margin: 0 }}>
                {submitError}
              </p>
            )}
          </div>
        )}

        {catalog.length === 0 ? (
          <div style={S.loading}>Loading catalog...</div>
        ) : (
          groupedCatalog.map(({ key, label, items }) => (
            <div key={key}>
              <h3 style={S.categoryHeader}>{label}</h3>
              {items.map((item) => {
                const max = item.maxQtyPerOrder ?? 5;
                const currentQty = quantities[item.itemId] || 0;
                const atMax = currentQty >= max;
                return (
                  <div key={item.itemId} style={S.itemRow}>
                    <span style={S.itemName}>{item.name}</span>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {currentQty > 0 ? (
                          <>
                            <button
                              style={S.qtyButton}
                              onClick={() => decrementQty(item.itemId)}
                            >
                              −
                            </button>
                            <span style={S.qtyValue}>{currentQty}</span>
                            <button
                              style={{
                                ...S.qtyButton,
                                ...(atMax ? { opacity: 0.35, cursor: "not-allowed" } : {}),
                              }}
                              onClick={() => !atMax && incrementQty(item.itemId)}
                              disabled={atMax}
                            >
                              +
                            </button>
                          </>
                        ) : (
                          <button
                            style={S.addButton}
                            onClick={() => incrementQty(item.itemId)}
                          >
                            + Add
                          </button>
                        )}
                      </div>
                      {max === 1 ? (
                        <span style={S.maxLabel}>max 1</span>
                      ) : currentQty > 0 ? (
                        <span style={S.maxLabel}>{currentQty}/{max}</span>
                      ) : (
                        <span style={S.maxLabel}>max {max}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {totalItems > 0 && (
        <div style={S.summaryBar}>
          <span style={S.summaryText}>
            {totalItems} item{totalItems !== 1 ? "s" : ""} selected
          </span>
          <button
            style={{
              ...S.submitButton,
              ...(phase === "submitting" ? { opacity: 0.7 } : {}),
            }}
            onClick={submitOrder}
            disabled={phase === "submitting"}
          >
            {phase === "submitting" ? "Submitting..." : "Submit Order →"}
          </button>
        </div>
      )}
    </div>
  );
}
